import React from 'react';
import { View, ScrollView, StyleSheet, Alert } from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Button,
  List,
  Avatar,
  Divider,
  useTheme,
  Text,
} from 'react-native-paper';
import { MaterialIcons } from '@expo/vector-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types';
import type { RootState } from '../store';

type NavigationProp = StackNavigationProp<RootStackParamList>;

// Fish species mapping
const FISH_SPECIES = [
  { id: 'Mahi', name: 'Mahi Mahi' },
  { id: 'Tuna', name: 'Yellowfin Tuna' },
  { id: 'Wahoo', name: 'Wahoo' },
  { id: 'Sailfish', name: 'Sailfish' },
  { id: 'Marlin', name: 'Blue Marlin' },
  { id: 'Blackfin', name: 'Blackfin Tuna' },
  { id: 'Kingfish', name: 'King Fish' },
  { id: 'Snapper', name: 'Snapper' },
  { id: 'Grouper', name: 'Grouper' },
  { id: 'Other', name: 'Other' },
];

export default function BasicProfileScreen() {
  const theme = useTheme();
  const { user, logout, isSubscribed, subscriptionStatus } = useAuth();
  const navigation = useNavigation<NavigationProp>();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  // Get catch reports from Redux
  const catchReports = useSelector((state: RootState) => state.fishing.catchReports || []);
  const tripPlans = useSelector((state: RootState) => state.trips.plans || []);

  // Calculate statistics
  const totalFishCaught = catchReports.reduce((sum, report) => sum + (report.count || 1), 0);
  const totalTrips = tripPlans.length;
  const successRate = totalTrips > 0 ? Math.round((catchReports.length / totalTrips) * 100) : 0;

  const handleSignOut = async () => {
    console.log('[ProfileScreen] handleSignOut called');
    try {
      setIsLoggingOut(true);
      console.log('[ProfileScreen] Signing out...');
      await logout();
      console.log('[ProfileScreen] Sign out successful');
    } catch (error) {
      console.error('[ProfileScreen] Sign out error:', error);
      setIsLoggingOut(false);
      Alert.alert(
        'Error',
        'Failed to sign out. Please try again.',
        [{ text: 'OK' }]
      );
    }
  };

  if (!user) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <Text>No user logged in</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* Profile Header */}
      <Card style={styles.profileCard}>
        <Card.Content style={styles.profileHeader}>
          <Avatar.Icon
            size={80}
            icon="account"
            style={{ backgroundColor: theme.colors.primary }}
          />
          <View style={styles.profileInfo}>
            <Title>{user.displayName || 'Ocean Explorer'}</Title>
            <Paragraph>{user.email}</Paragraph>
            <View style={styles.subscriptionBadge}>
              <MaterialIcons
                name={isSubscribed ? 'verified' : 'timer'}
                size={16}
                color={isSubscribed ? '#4CAF50' : theme.colors.secondary}
              />
              <Paragraph style={[
                styles.subscriptionText,
                isSubscribed && { color: '#4CAF50' }
              ]}>
                {isSubscribed ? 'Pro Member' : 'Free Account'}
              </Paragraph>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Quick Stats */}
      <Card style={styles.statsCard}>
        <Card.Content>
          <Title style={styles.sectionTitle}>Fishing Statistics</Title>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{totalTrips}</Text>
              <Text style={styles.statLabel}>Total Trips</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{totalFishCaught}</Text>
              <Text style={styles.statLabel}>Fish Caught</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{successRate}%</Text>
              <Text style={styles.statLabel}>Success Rate</Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Menu Items */}
      <Card style={styles.menuCard}>
        <List.Item
          title={isSubscribed ? 'Manage Subscription' : 'Upgrade to Pro'}
          description={isSubscribed ? 'Manage your subscription' : 'Unlock all premium features'}
          left={(props) => <List.Icon {...props} icon={isSubscribed ? 'card-account-details' : 'star'} />}
          right={(props) => <List.Icon {...props} icon="chevron-right" />}
          onPress={() => navigation.navigate('Subscription')}
        />
        <Divider />
        <List.Item
          title="My Waypoints"
          description="Manage your private waypoints"
          left={(props) => <List.Icon {...props} icon="map-marker" />}
          right={(props) => <List.Icon {...props} icon="chevron-right" />}
          onPress={() => navigation.navigate('Waypoints')}
        />
        <Divider />
        <List.Item
          title="Settings"
          description="App preferences and units"
          left={(props) => <List.Icon {...props} icon="cog" />}
          right={(props) => <List.Icon {...props} icon="chevron-right" />}
          onPress={() => navigation.navigate('Settings')}
        />
        <Divider />
        <List.Item
          title="Help & Support"
          description="Get help with the app"
          left={(props) => <List.Icon {...props} icon="help-circle" />}
          right={(props) => <List.Icon {...props} icon="chevron-right" />}
          onPress={() => Alert.alert('Help', 'Help section coming soon!')}
        />
      </Card>

      {/* Sign Out Button */}
      <Button
        mode="outlined"
        onPress={handleSignOut}
        style={styles.signOutButton}
        textColor={theme.colors.error}
        icon="logout"
        loading={isLoggingOut}
        disabled={isLoggingOut}
      >
        {isLoggingOut ? 'Signing Out...' : 'Sign Out'}
      </Button>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileCard: {
    margin: 16,
    marginBottom: 8,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  profileInfo: {
    marginLeft: 16,
    flex: 1,
  },
  subscriptionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  subscriptionText: {
    marginLeft: 4,
    fontSize: 14,
  },
  statsCard: {
    margin: 16,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0066cc',
  },
  statLabel: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  menuCard: {
    margin: 16,
    marginBottom: 8,
  },
  signOutButton: {
    margin: 16,
    marginBottom: 32,
  },
  catchesContainer: {
    marginTop: 8,
  },
  catchItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  catchInfo: {
    flex: 1,
  },
  catchSpecies: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  catchDetails: {
    fontSize: 14,
    color: '#666',
    marginBottom: 2,
  },
  catchNotes: {
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
  },
  viewAllButton: {
    marginTop: 8,
  },
});